import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ModalContent, ModalHeader, RegularButton, Typography } from 'fronton-react';
import { MockStoreLinks } from '__mocks__';
import { checkScreenType } from 'utils';
import { GAClickButton } from 'utils/analytics';
import { selectPageModelTitle } from '../../../store/pageModel';
import { selectWindowType, WindowTypes } from '../../../store/pageStore';
import { getPath } from '../../../selectors';
import {
	addressStyles,
	ModalRow,
	nameStyles,
	buttonStyles,
	modalContentStyles,
	modalHeaderStyles,
} from './styles';

type TStoreData = {
	selectedStoreCode?: number;
	code: number;
	name: string;
	address: string | null;
};

type ModalListItemProps = TStoreData;

type StoreSelectionModalContentProps = {
	storesList: TStoreData[];
	selectedStoreCode?: number;
};

const StoreSelectionModalHeader = () => (
	<ModalHeader label="" title={'Выберите ближайший\u00A0магазин'} className={modalHeaderStyles} />
);

const ModalListItem = ({ selectedStoreCode, code, name, address }: ModalListItemProps) => {
	const currentWindowType = useSelector(selectWindowType);
	const isMobile = checkScreenType(currentWindowType, WindowTypes.mobile);

	const pathname = useSelector(getPath);
	const title = useSelector(selectPageModelTitle);

	const storeNameVariant = useMemo(() => (isMobile ? 'm' : 's'), [isMobile]);

	const isStoreSelected = useMemo(() => selectedStoreCode === code, [selectedStoreCode, code]);

	const storeLink = useMemo(() => {
		const { link } = MockStoreLinks.find(({ code: storeCode }) => storeCode === code) || {
			link: null,
		};
		const path = pathname.split('/').slice(0, 3).join('/');
		return link ? `${path}/${link}/` : `${path}/`;
	}, [code, pathname]);

	const clickHandler = () => {
		GAClickButton({
			elementBlock: 'servicePage',
			elementName: `${title} / modal`,
			elementText: name,
		});
	};

	return (
		<ModalRow address={address} data-qa="modal-store">
			<Typography
				className={nameStyles}
				variant={storeNameVariant}
				size="body_accent"
				as="h3"
				data-qa="modal-store-name"
			>
				{name}
			</Typography>
			{address && (
				<Typography
					className={addressStyles}
					variant="s"
					size="body_long"
					as="p"
					data-qa="modal-store-address"
				>
					{address}
				</Typography>
			)}
			<RegularButton
				className={buttonStyles}
				size="s"
				variant="accent"
				disabled={isStoreSelected}
				href={storeLink}
				onClick={clickHandler}
				data-qa="store-selection-modal-btn"
			>
				{isStoreSelected ? 'Выбран' : 'Выбрать'}
			</RegularButton>
		</ModalRow>
	);
};

const StoreSelectionModalContent = ({
	selectedStoreCode,
	storesList,
}: StoreSelectionModalContentProps) => (
	<ModalContent className={modalContentStyles}>
		{storesList.map(store => (
			<ModalListItem
				key={`store-${store.code}`}
				selectedStoreCode={selectedStoreCode}
				{...store}
			/>
		))}
	</ModalContent>
);

export const StoreSelectionModal = (data: StoreSelectionModalContentProps) => (
	<>
		<StoreSelectionModalHeader />
		<StoreSelectionModalContent {...data} />
	</>
);
